import type { Editor, Range } from '@tiptap/core'
import type { User } from '../../lib/types.ts'

/**
 * The `@` popup inside the note editor.
 *
 * Written by hand instead of pulling in a positioning library: the list has two entries and lives
 * next to the caret. A floating-UI dependency for that would be more code to keep pinned than the
 * thing it positions (RULES §6 — the Tiptap/Yjs versions all move together).
 *
 * This is browser-only decoration. It does not touch the schema — the `mention` node is defined in
 * the shared `collab/schema.ts`, so what the server renders and what the editor produces stay
 * identical (ADR 0006).
 */

export function handleOf(user: User): string {
  return user.displayName.toLowerCase().replace(/\s+/g, '')
}

interface SuggestionProps {
  items: User[]
  command: (attrs: { id: string; label: string }) => void
  clientRect?: (() => DOMRect | null) | null
  editor: Editor
}

export function mentionSuggestion(getUsers: () => User[]): Record<string, unknown> {
  return {
    char: '@',
    // `allowedPrefixes` left at its default (whitespace): the option takes plain characters, and a
    // RegExp here gets string-joined into a broken character class that silently matches nothing.

    items: ({ query }: { query: string }) =>
      getUsers().filter((u) => handleOf(u).startsWith(query.toLowerCase())),

    command: ({
      editor,
      range,
      props,
    }: {
      editor: Editor
      range: Range
      props: { id: string; label: string }
    }) => {
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          { type: 'mention', attrs: props },
          { type: 'text', text: ' ' },
        ])
        .run()
    },

    render: () => {
      let element: HTMLDivElement | null = null
      let active = 0
      let current: SuggestionProps | null = null

      const paint = (): void => {
        const host = element
        if (!host || !current) return
        host.replaceChildren()

        current.items.forEach((user, index) => {
          const option = document.createElement('div')
          option.id = `mention-option-${user.id}`
          option.setAttribute('role', 'option')
          option.setAttribute('aria-selected', String(index === active))
          option.className =
            index === active ? 'mention-option mention-option-active' : 'mention-option'

          // textContent, never innerHTML: displayName is user-supplied (settings, and the MCP
          // rename tool), so interpolating it into markup would run whatever the other person
          // typed into their own name the moment anyone opens the `@` popup.
          const name = document.createElement('span')
          name.textContent = user.displayName
          const handle = document.createElement('span')
          handle.className = 'mention-handle'
          handle.textContent = `@${handleOf(user)}`
          option.append(name, handle)

          // mousedown, not click: click lands after the editor has already lost focus.
          option.addEventListener('mousedown', (event) => {
            event.preventDefault()
            current?.command({ id: String(user.id), label: handleOf(user) })
          })
          host.appendChild(option)
        })
      }

      /** Points the editor at the highlighted option, the way a combobox is expected to. */
      const describeEditor = (): void => {
        const dom = current?.editor.view.dom
        if (!dom) return
        dom.setAttribute('aria-expanded', 'true')
        dom.setAttribute('aria-controls', 'mention-popup')
        const user = current?.items[active]
        if (user) dom.setAttribute('aria-activedescendant', `mention-option-${user.id}`)
      }

      const undescribeEditor = (): void => {
        const dom = current?.editor.view.dom
        if (!dom) return
        dom.removeAttribute('aria-expanded')
        dom.removeAttribute('aria-controls')
        dom.removeAttribute('aria-activedescendant')
      }

      const place = (): void => {
        const rect = current?.clientRect?.()
        if (!element || !rect) return
        element.style.left = `${Math.round(rect.left)}px`
        // Below the caret, flipped above when the line sits near the bottom of the window.
        const below = rect.bottom + 4
        const height = element.offsetHeight
        element.style.top =
          below + height + 8 <= window.innerHeight
            ? `${Math.round(below)}px`
            : `${Math.round(rect.top - height - 4)}px`
      }

      return {
        onStart: (props: SuggestionProps) => {
          current = props
          active = 0
          element = document.createElement('div')
          element.className = 'mention-popup'
          // The React sibling of this popup (ui/MentionField.tsx) is a proper combobox; this one
          // had no roles at all, so the same feature was announced in one place and silent in the
          // other. It cannot reuse that component — Tiptap drives this through a ProseMirror
          // plugin, not React state — but it can describe itself the same way.
          element.id = 'mention-popup'
          element.setAttribute('role', 'listbox')
          element.setAttribute('aria-label', 'Osoby')
          document.body.appendChild(element)
          paint()
          place()
          describeEditor()
        },

        onUpdate: (props: SuggestionProps) => {
          current = props
          active = 0
          paint()
          place()
          describeEditor()
        },

        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (!current || current.items.length === 0) return false

          if (event.key === 'ArrowDown') {
            active = (active + 1) % current.items.length
            paint()
            describeEditor()
            return true
          }
          if (event.key === 'ArrowUp') {
            active = (active - 1 + current.items.length) % current.items.length
            paint()
            describeEditor()
            return true
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            const user = current.items[active]
            if (user) current.command({ id: String(user.id), label: handleOf(user) })
            return true
          }
          if (event.key === 'Escape') {
            undescribeEditor()
            element?.remove()
            element = null
            return true
          }
          return false
        },

        onExit: () => {
          undescribeEditor()
          element?.remove()
          element = null
          current = null
        },
      }
    },
  }
}
